import React, {useRef} from 'react'
import {Box, Divider, Typography} from '@mui/material'
import {keyframes} from '@emotion/react'
import Card from 'src/components-new/layout/Card'
import StatusPill from 'src/components-new/primitives/StatusPill'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import {formatNextDoseWhen, getSecondaryAssumptionKey, SecondaryAssumption} from 'src/constants/secondaryAssumptions'
import QuickAnswerButtonsNew from 'src/pages-new/secondaryAssumptions/QuickAnswerButtonsNew'

interface NextDosesCardProps {
  nextDoses: SecondaryAssumption[]
  drugNameOf: (assumption: SecondaryAssumption) => string
  denyingKey?: string
  // A dose just answered and flying into the history (see useFlyToHistory): its row keeps its
  // space, invisible, until the following dose comes back, so the card doesn't collapse and jump.
  placeholderKey?: string
  onDeny: (assumption: SecondaryAssumption) => void
  onConfirm: (assumption: SecondaryAssumption) => void
}

const appear = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: none; }
`

/**
 * The upcoming dose of each accessory therapy (RF1.4/RF3.1.1 derived rows), shown above the
 * "Terapie Accessorie" table rather than as its first rows: the table is then only doses whose
 * time has come, each with its own date/time and state. Same ✗/✓ controls as a table row — a
 * dose can be answered ahead of time too.
 */
export default function NextDosesCard({
  nextDoses,
  drugNameOf,
  denyingKey,
  placeholderKey,
  onDeny,
  onConfirm
}: NextDosesCardProps): JSX.Element | null {
  // Doses already there when the card first showed up don't animate; one that arrives later (the
  // following dose, after an answer) fades in where the answered one was.
  const initialKeys = useRef<Set<string> | null>(null)
  if (initialKeys.current === null && nextDoses.length > 0) {
    initialKeys.current = new Set(nextDoses.map(getSecondaryAssumptionKey))
  }

  if (nextDoses.length === 0) return null

  return (
    // Muted, neutral look: these doses haven't happened yet, so they shouldn't compete with the
    // history below for attention.
    <Card
      title={nextDoses.length === 1 ? 'Prossima assunzione' : 'Prossime assunzioni'}
      sx={{mb: 2, backgroundColor: NEUTRAL_SCALE[50]}}
    >
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
        {nextDoses.map((dose, index) => {
          const key = getSecondaryAssumptionKey(dose)
          const isPlaceholder = key === placeholderKey
          const isNew = !isPlaceholder && !initialKeys.current?.has(key)
          return (
            <React.Fragment key={key}>
              {index > 0 && <Divider />}
              <Box
                data-next-dose-key={key}
                aria-hidden={isPlaceholder || undefined}
                sx={{
                  display: 'grid',
                  alignItems: 'center',
                  columnGap: 2,
                  rowGap: 0.5,
                  // the state in the middle of the row on desktop, under the drug name on mobile
                  gridTemplateColumns: {xs: '1fr auto', md: '1fr auto 1fr'},
                  gridTemplateAreas: {xs: '"dose answer" "state answer"', md: '"dose state answer"'},
                  visibility: isPlaceholder ? 'hidden' : undefined,
                  ...(isNew && {
                    animation: `${appear} 1000ms ease-out both`,
                    '@media (prefers-reduced-motion: reduce)': {animation: 'none'},
                  }),
                }}
              >
                <Box sx={{gridArea: 'dose', minWidth: 0}}>
                  <Typography sx={{fontWeight: 600, color: NEUTRAL_SCALE[700]}}>{drugNameOf(dose)}</Typography>
                  <Typography variant="body2" sx={{color: NEUTRAL_SCALE[500]}}>{formatNextDoseWhen(dose.expectedDate) || '—'}</Typography>
                </Box>
                <Box sx={{gridArea: 'state', justifySelf: {xs: 'start', md: 'center'}}}>
                  <StatusPill label="Pianificata" color="info" />
                </Box>
                <Box sx={{gridArea: 'answer', justifySelf: 'end'}}>
                  <QuickAnswerButtonsNew
                    denyLoading={denyingKey === key}
                    onDeny={() => onDeny(dose)}
                    onConfirm={() => onConfirm(dose)}
                  />
                </Box>
              </Box>
            </React.Fragment>
          )
        })}
      </Box>
    </Card>
  )
}
